import React from "react";
import { ArrowRight, CheckCircle2, Smile, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { heroImage, heroStats } from "../mock";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section id="home" className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 overflow-hidden">
      {/* Soft background blobs */}
      <div className="absolute -top-20 -right-32 w-[520px] h-[520px] bg-orange-200/40 rounded-full blur-3xl" />
      <div className="absolute top-40 -left-32 w-[460px] h-[460px] bg-green-200/40 rounded-full blur-3xl" />
      <div className="absolute top-24 left-1/2 w-2 h-2 rounded-full bg-orange-400/70" />
      <div className="absolute top-60 left-10 w-2 h-2 rounded-full bg-green-500/70" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
        <div className="reveal">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-orange-100 text-[#ee6c4d] text-xs font-semibold tracking-wide soft-pulse">
            <Sparkles className="w-3.5 h-3.5 twinkle" />
            Patna&apos;s Trusted Pediatric Therapy Centre
          </span>
          <h1 className="mt-5 font-serif text-4xl sm:text-5xl md:text-6xl lg:text-[68px] leading-[1.05] tracking-tight text-[#122b45]">
            <span className="stroke-green">Nanhe Kadam</span>
            <br />
            <span className="stroke-orange text-[#ee6c4d]">Therapy Centre</span>
          </h1>
          <p className="mt-6 text-base sm:text-[17px] leading-relaxed text-slate-600 max-w-xl reveal reveal-delay-200">
            Every small step matters — and we help your child take it. Compassionate, child-first therapy for speech, behaviour, motor & learning development by experienced specialists in Patna.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3 reveal reveal-delay-300">
            <Button
              onClick={() => scrollTo("#contact")}
              className="group bg-[#122b45] hover:bg-[#0d2138] text-white rounded-full h-12 px-6 text-[15px]"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => scrollTo("#services")}
              variant="ghost"
              className="text-[#122b45] hover:bg-orange-50 rounded-full h-12 px-6 text-[15px] border border-transparent hover:border-orange-100"
            >
              Explore Our Services
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap gap-8 sm:gap-10 reveal reveal-delay-400">
            {heroStats.map((s, idx) => (
              <div key={s.label} className={`reveal reveal-delay-${(idx + 4) * 100}`}>
                <div className="font-serif text-3xl md:text-4xl text-[#ee6c4d] leading-none">{s.value}</div>
                <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right image with floating cards */}
        <div className="relative reveal-right reveal-delay-200">
          <div className="relative rounded-[28px] overflow-hidden shadow-[0_30px_60px_-20px_rgba(18,43,69,0.25)] aspect-[5/6] bg-orange-100">
            <img src={heroImage} alt="Happy child during pediatric therapy session at Nanhe Kadam Therapy Centre, Patna" className="w-full h-full object-cover" />
          </div>

          {/* Floating chips */}
          <div className="absolute -top-3 left-2 lg:-left-10 bg-white rounded-2xl shadow-lg px-3 py-2.5 lg:px-4 lg:py-3 flex items-center gap-2 lg:gap-3 floaty">
            <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
              <Smile className="w-4 h-4 lg:w-5 lg:h-5" />
            </div>
            <div>
              <div className="text-[9px] lg:text-[10px] uppercase tracking-wider text-slate-400">Approach</div>
              <div className="text-xs lg:text-sm font-semibold text-[#122b45]">Play-based · Child-led</div>
            </div>
          </div>

          <div className="absolute top-1/2 right-2 lg:-right-8 bg-white rounded-2xl shadow-lg px-3 py-2.5 lg:px-4 lg:py-3 flex items-center gap-2 lg:gap-3 floaty" style={{animationDelay: "1s"}}>
            <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-orange-100 text-orange-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 lg:w-5 lg:h-5" />
            </div>
            <div>
              <div className="text-[9px] lg:text-[10px] uppercase tracking-wider text-slate-400">Sessions</div>
              <div className="text-xs lg:text-sm font-semibold text-[#122b45]">1-on-1 Expert Care</div>
            </div>
          </div>

          <div className="absolute -bottom-3 left-4 lg:left-6 bg-white rounded-2xl shadow-lg px-3 py-2.5 lg:px-4 lg:py-3 flex items-center gap-2 lg:gap-3 floaty" style={{animationDelay: "2s"}}>
            <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 lg:w-5 lg:h-5" />
            </div>
            <div>
              <div className="text-[9px] lg:text-[10px] uppercase tracking-wider text-slate-400">Certified</div>
              <div className="text-xs lg:text-sm font-semibold text-[#122b45]">Trusted Therapists</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
